import React, { useContext, useState } from 'react';
import { Box, FormControlLabel, Switch } from '@mui/material';
import { Colors } from '../../utils/theme';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';
import {
  ICountry,
  ICurrency,
  useCountriesQuery,
  useCurrenciesQuery,
  useCompaniesNamesQuery,
  useSectorsQuery,
  useInvestmentFocusesQuery,
  ICompany,
  useCreateStockMutation,
  IStockInput,
  ISector,
  IInvestmentFocus
} from '../../generated/graphql';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import {
  getNumberInput,
  getObjectInputId,
  getStringInput,
  stockCodeExists
} from '../../utils/clientUtils';
import DialogBox from '../Common/Layout/DialogBox';
import SaveButton from '../Common/Buttons/SaveButton';
import CancelButton from '../Common/Buttons/CancelButton';
import InputComponent from '../Common/Fields/InputComponent';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';
import Loader from '../Common/Loader';

type IProps = {
  handleClose: () => void;
};

export type IErrors = {
  stockCode: string | null;
  stockName: string | null;
  company: string | null;
  inclusion: string | null;
};

const defaultErrorsObj: IErrors = {
  stockCode: null,
  stockName: null,
  company: null,
  inclusion: null
};

export const errorTexts = {
  stockName: 'Stock Name too short!',
  stockCode: 'Stock code should start with C!',
  stockCodeExists: 'Stock code already exists!',
  company: 'Company is required!',
  inclusion: 'Index Inclusion is required!'
};

const StockForm: React.FunctionComponent<IProps> = ({ handleClose }: IProps) => {
  const countries = useCountriesQuery();
  const currencies = useCurrenciesQuery();
  const companies = useCompaniesNamesQuery();
  const sectors = useSectorsQuery();
  const investmentFocuses = useInvestmentFocusesQuery();
  const [country, setCountry] = useState<ICountry | null>(null);
  const [currency, setCurrency] = useState<ICurrency | null>(null);
  const [cusip, setCusip] = useState('');
  const [isinCode, setIsinCode] = useState('');
  const [bloomergTicker, setBloomergTicker] = useState('');
  const [stockCode, setStockCode] = useState('');
  const [exchangeCode, setExchangeCode] = useState('');
  const [stockName, setStockName] = useState('');
  const [reit, setReit] = useState(false);
  const [inclusionDate, setInclusionDate] = useState<Date | null>(null);
  const [icbSupersectorCode, setIcbSupersectorCode] = useState('');
  const [icbIndustryCode, setIcbIndustryCode] = useState('');
  const [icbSectorCode, setIcbSectorCode] = useState('');
  const [icbSubsectorSectorCode, setIcbSubsectorCode] = useState('');
  const [sedol, setSedol] = useState('');
  const [reutersCode, setReutersCode] = useState('');
  const [sAndP, setSAndP] = useState('');
  const [company, setCompany] = useState<ICompany | null>(null);
  const [sector, setSector] = useState<ISector | null>(null);
  const [subsector, setSubsector] = useState<ISector | null>(null);
  const [investmentFocus, setInvestmentFocus] = useState<IInvestmentFocus | null>(null);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [createStock, { loading: createStockLoading }] = useCreateStockMutation();
  const { dispatch } = useContext(AppContext);

  const handleStockCodeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.target;
    if (value.length > 0) {
      if (value[0].toLowerCase() === 'c') {
        setFormErrors({ ...formErrors, stockCode: null });
      } else {
        setFormErrors({ ...formErrors, stockCode: errorTexts.stockCode });
      }
    } else {
      setFormErrors({ ...formErrors, stockCode: null });
    }
    setStockCode(value.trimStart());
  };

  const handleStockNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.target;
    setFormErrors({
      ...formErrors,
      stockName: value.trim().length < 2 ? errorTexts.stockName : null
    });
    setStockName(value.trimStart());
  };

  const handleCompanyChange = (_: unknown, newValue: React.SetStateAction<ICompany | null>) => {
    if (newValue) {
      setFormErrors({ ...formErrors, company: null });
    } else {
      setFormErrors({ ...formErrors, company: errorTexts.company });
    }
    setCompany(newValue);
  };

  const handleDateChange = (dateInput: Date | null) => {
    if (dateInput !== null) {
      setFormErrors({ ...formErrors, inclusion: null });
    } else {
      setFormErrors({ ...formErrors, inclusion: errorTexts.inclusion });
    }
    setInclusionDate(dateInput);
  };

  const handleSubmit = () => {
    let hasErrors = false;
    const errors: IErrors = {
      company: null,
      stockCode: null,
      stockName: null,
      inclusion: null
    };
    if (stockName.trim().length < 2) {
      hasErrors = true;
      errors.stockName = errorTexts.stockName;
    }
    if (stockCode.trim().length > 0) {
      if (stockCode[0].toLowerCase() !== 'c') {
        hasErrors = true;
        errors.stockCode = errorTexts.stockCode;
      } else {
        const codeExists = stockCodeExists(stockCode);
        if (codeExists) {
          hasErrors = true;
          errors.stockCode = errorTexts.stockCodeExists;
        }
      }
    }
    if (company === null) {
      hasErrors = true;
      errors.company = errorTexts.company;
    }
    if (inclusionDate === null) {
      hasErrors = true;
      errors.inclusion = errorTexts.inclusion;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      submitStock();
    }
  };

  const submitStock = () => {
    const input: IStockInput = {
      name: stockName,
      company_id: parseInt(company?.id as string),
      is_reit: reit,
      start_date: (inclusionDate as Date).toString(),
      bloomberg_ticker: getStringInput(bloomergTicker),
      cusip: getStringInput(cusip),
      country_id: getObjectInputId(country?.id),
      currency_id: getObjectInputId(currency?.id),
      isin_code: getStringInput(isinCode),
      reuters_code: getStringInput(reutersCode),
      code: getStringInput(stockCode),
      exchange_code: getStringInput(exchangeCode),
      sedol: getStringInput(sedol),
      snl_code: getStringInput(sAndP),
      investment_focus_id: getObjectInputId(investmentFocus?.id),
      icb_industry_code: getNumberInput(icbIndustryCode),
      icb_sector_code: getNumberInput(icbSectorCode),
      icb_subsector_code: getNumberInput(icbSubsectorSectorCode),
      icb_supersector_code: getNumberInput(icbSupersectorCode),
      sector_id: getObjectInputId(sector?.id),
      subsector_id: getObjectInputId(subsector?.id)
    };

    createStock({
      variables: {
        input
      },
      update() {
        // Subscription will handle updating the list
      }
    })
      .then((result) => {
        if (result.data?.result) {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              text: `Stock ${result.data.result.name} created`,
              severity: 'success',
              autoHide: 2000
            }
          });
          handleClose();
        }
      })
      .catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      });
  };

  return (
    <DialogBox id="stock-form" width={1400} title={'Create Stock'}>
      <Box sx={{ display: 'flex' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            height: '420px',
            pt: '60px',
            gap: '24px 104px'
          }}
        >
          <Box
            sx={{
              gridColumn: 'span 1',
              display: 'grid',
              gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
              pl: '80px',
              gap: '0px 24px'
            }}
          >
            <AutocompleteComponent
              id="country-autocomplete"
              label="Country"
              options={(countries.data?.result as never[]) || []}
              values={country}
              onChange={(_, newValue) => setCountry(newValue)}
              getOptionLabel={(option: ICountry) => option?.name}
              additionalStyle={{ gridColumn: 'span 6' }}
            />
            <AutocompleteComponent
              id="currency-autocomplete"
              label="Currency Code"
              options={(currencies.data?.result as never[]) || []}
              values={currency}
              onChange={(_, newValue) => setCurrency(newValue)}
              getOptionLabel={(option: ICurrency) => option?.code}
              additionalStyle={{ gridColumn: 'span 6' }}
            />
            <AutocompleteComponent
              id="investment-focus-autocomplete"
              label="Investment Focus"
              options={(investmentFocuses.data?.result as never[]) || []}
              values={investmentFocus}
              onChange={(_, newValue) => setInvestmentFocus(newValue)}
              getOptionLabel={(option: IInvestmentFocus) => option?.name}
              additionalStyle={{ gridColumn: 'span 6' }}
            />
            <InputComponent
              id={'cusip'}
              label="CUSIP"
              value={cusip || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCusip(e.target.value)}
              additionalStyle={{ gridColumn: 'span 6' }}
            />
            <InputComponent
              id={'isin-code'}
              label={'ISIN code'}
              value={isinCode || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsinCode(e.target.value)}
              additionalStyle={{ gridColumn: 'span 6' }}
            />
            <InputComponent
              id={'bloomberg-ticker'}
              label={'Bloomberg Ticker'}
              value={bloomergTicker || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setBloomergTicker(e.target.value)
              }
              additionalStyle={{ gridColumn: 'span 6' }}
            />
            <InputComponent
              id={'stock-code'}
              label={'Stock Code'}
              value={stockCode || ''}
              onChange={handleStockCodeChange}
              additionalStyle={{ gridColumn: 'span 6' }}
            />
            <InputComponent
              id={'exchange-code'}
              label={'Exchange Code'}
              value={exchangeCode || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExchangeCode(e.target.value)}
              additionalStyle={{ gridColumn: 'span 6' }}
            />
            <InputComponent
              id={'stock-name'}
              label={'Stock Name'}
              value={stockName || ''}
              onChange={handleStockNameChange}
              errorMsg={formErrors.stockName}
              additionalStyle={{ gridColumn: 'span 12' }}
            />
          </Box>
          <Box
            sx={{
              gridColumn: 'span 1',
              display: 'grid',
              gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
              pr: '80px',
              gap: '0px 24px'
            }}
          >
            <InputComponent
              id={'icb-supersector-code'}
              label={'ICB Supersector Code'}
              value={icbSupersectorCode || ''}
              type="number"
              onChange={(e) => setIcbSupersectorCode(e.target.value)}
              additionalStyle={{ gridColumn: 'span 6' }}
            />
            <InputComponent
              id={'icb-industry-code'}
              label={'ICB Industry Code'}
              value={icbIndustryCode || ''}
              type="number"
              onChange={(e) => setIcbIndustryCode(e.target.value)}
              additionalStyle={{ gridColumn: 'span 6' }}
            />
            <InputComponent
              id={'icb-sector-code'}
              label={'ICB Sector Code'}
              value={icbSectorCode || ''}
              type="number"
              onChange={(e) => setIcbSectorCode(e.target.value)}
              additionalStyle={{ gridColumn: 'span 6' }}
            />
            <InputComponent
              id={'icb-subsector-code'}
              label={'ICB Subsector Code'}
              value={icbSubsectorSectorCode || ''}
              type="number"
              onChange={(e) => setIcbSubsectorCode(e.target.value)}
              additionalStyle={{ gridColumn: 'span 6' }}
            />
            <AutocompleteComponent
              id="sectors-autocomplete"
              label="Sector"
              options={(sectors.data?.result as never[]) || []}
              values={sector}
              onChange={(_, newValue) => setSector(newValue)}
              getOptionLabel={(option: ISector) => option?.name}
              additionalStyle={{ gridColumn: 'span 6' }}
            />
            <AutocompleteComponent
              id="subsectors-autocomplete"
              label="Subsector"
              options={(sectors.data?.result as never[]) || []}
              values={subsector}
              onChange={(_, newValue) => setSubsector(newValue)}
              getOptionLabel={(option: ISector) => option?.name}
              additionalStyle={{ gridColumn: 'span 6' }}
            />
            <InputComponent
              id={'sedol'}
              label={'SEDOL'}
              value={sedol || ''}
              onChange={(e) => setSedol(e.target.value)}
              additionalStyle={{ gridColumn: 'span 4' }}
            />
            <InputComponent
              id={'reuters-code'}
              label={'Reuters Code'}
              value={reutersCode || ''}
              onChange={(e) => setReutersCode(e.target.value)}
              additionalStyle={{ gridColumn: 'span 4' }}
            />
            <InputComponent
              id={'s-and-p'}
              label={'S&P'}
              value={sAndP || ''}
              onChange={(e) => setSAndP(e.target.value)}
              additionalStyle={{ gridColumn: 'span 4' }}
            />
            <AutocompleteComponent
              id="companies-autocomplete"
              label="Company name"
              options={(companies.data?.result as never[]) || []}
              values={company}
              errorMsg={formErrors.company}
              onChange={handleCompanyChange}
              getOptionLabel={(option: ICompany) => option?.name}
              additionalStyle={{ gridColumn: 'span 7' }}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <EpraDatePicker
                id="index-inclusion-picker"
                label={'Index Inclusion'}
                value={inclusionDate}
                format={'dd/MM/yyyy'}
                errorMsg={formErrors.inclusion}
                clearable={false}
                onChange={handleDateChange}
                sx={{ gridColumn: 'span 5' }}
              />
            </LocalizationProvider>
          </Box>
        </Box>
      </Box>
      <Box>
        <Box sx={{ pl: '80px' }}>
          <FormControlLabel
            value={reit}
            control={<Switch color="primary" checked={reit} onChange={() => setReit(!reit)} />}
            id="reit"
            label="REIT"
            labelPlacement="start"
            style={{
              marginLeft: 0,
              color: Colors.main,
              fontSize: '18px'
            }}
          />
        </Box>
        <Box className="centralize" sx={{ pb: '40px', pt: '110px' }}>
          <CancelButton
            id="cancel-stock-button"
            data-testid="cancel-stock-button"
            onClick={handleClose}
            text={'Cancel'}
          />
          <SaveButton
            id="save-stock-button"
            data-testid="save-stock-button"
            onClick={handleSubmit}
            text={'Create'}
          />
        </Box>
      </Box>
      <Loader
        loading={
          countries.loading ||
          companies.loading ||
          currencies.loading ||
          sectors.loading ||
          investmentFocuses.loading ||
          createStockLoading
        }
      />
    </DialogBox>
  );
};

export default StockForm;
